import { useId } from 'react';
/* The icon set, lifted from the artboards.
   All 24×24, stroked at 2.75 — the weight the canvas draws them at, which is
   what keeps them legible at 14px on a phone held at arm's length in the sun. */

interface Props { size?: number; className?: string }

function S({ size = 19, className, children }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true" focusable="false"
    >
      {children}
    </svg>
  );
}

export const IconClock = (p: Props) => <S {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></S>;
export const IconCalendar = (p: Props) => <S {...p}><rect x="4" y="5" width="16" height="16" rx="4" /><path d="M8 3v4M16 3v4M4 11h16" /></S>;
export const IconPin = (p: Props) => <S {...p}><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" /><circle cx="12" cy="10" r="2.4" /></S>;
export const IconPinSolid = (p: Props) => <S {...p}><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" /></S>;
export const IconImages = (p: Props) => <S {...p}><rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="9.5" cy="9.5" r="1.6" /><path d="M5 17l4.5-4 4 3.2 2.5-2.3L19 17" /></S>;
export const IconSun = (p: Props) => (
  <S {...p}>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
  </S>
);
export const IconMoon = (p: Props) => <S {...p}><path d="M20.5 14.3A8.5 8.5 0 0 1 9.7 3.5 8.5 8.5 0 1 0 20.5 14.3z" /></S>;
/* Weather (D67). Enough glyphs to cover what the WMO codes collapse to for a
   coast at 8°N in November — clear, cloud, rain, storm, fog — and no more:
   `IconSun` and `IconMoon` above already carry clear-sky day and night, so a
   code only needs a new shape where the existing set would lie about it.
   Snow and freezing codes resolve onto these too; see `lib/weather.ts` for why
   the table is total rather than only the reachable half. */
export const IconCloud = (p: Props) => (
  <S {...p}><path d="M7.5 19a4.5 4.5 0 0 1-.3-9 6 6 0 0 1 11.4 1.6A3.8 3.8 0 0 1 17.5 19z" /></S>
);
/* Sun behind cloud. The disc sits top-left and is clipped by the cloud rather
   than drawn whole, because at 15px a complete circle behind a complete cloud
   reads as a balloon on a string. */
export const IconPartlyCloud = (p: Props) => (
  <S {...p}>
    <circle cx="8.5" cy="7.5" r="3.2" />
    <path d="M8.5 1.8v1.4M2.8 7.5h1.4M4.3 3.3l1 1M12.7 3.3l-1 1" />
    <path d="M10 19a4 4 0 0 1-.3-8 5.4 5.4 0 0 1 10.1 1.4A3.4 3.4 0 0 1 18.6 19z" />
  </S>
);
export const IconDrizzle = (p: Props) => (
  <S {...p}>
    <path d="M7.5 16a4.5 4.5 0 0 1-.3-9 6 6 0 0 1 11.4 1.6A3.8 3.8 0 0 1 17.5 16z" />
    <path d="M9.5 19v1.2M14.5 19v1.2" />
  </S>
);
export const IconRain = (p: Props) => (
  <S {...p}>
    <path d="M7.5 15a4.5 4.5 0 0 1-.3-9 6 6 0 0 1 11.4 1.6A3.8 3.8 0 0 1 17.5 15z" />
    <path d="M8.5 18l-1 3M12 18l-1 3M15.5 18l-1 3" />
  </S>
);
export const IconStorm = (p: Props) => (
  <S {...p}>
    <path d="M7.5 15a4.5 4.5 0 0 1-.3-9 6 6 0 0 1 11.4 1.6A3.8 3.8 0 0 1 17.5 15z" />
    <path d="M13 17l-3 3.4h3l-1 3.1" />
  </S>
);
export const IconFog = (p: Props) => (
  <S {...p}>
    <path d="M7.5 13.5a4.5 4.5 0 0 1-.3-9 6 6 0 0 1 11.4 1.6 3.8 3.8 0 0 1-1.1 7.4z" />
    <path d="M5 17h14M7 20.5h10" />
  </S>
);

export const IconBack = (p: Props) => <S {...p}><path d="M15 18l-6-6 6-6" /></S>;
export const IconForward = (p: Props) => <S {...p}><path d="M9 6l6 6-6 6" /></S>;
/**
 * The Spotify mark, so a guest can see where the tap is about to send them
 * before they take it (D57).
 *
 * **Monochrome, in `currentColor`.** Spotify's guidance allows the mark in
 * solid black or solid white as well as in green, and mono is the version that
 * belongs here: it inherits the page's ink, so it works in both themes and on
 * the primary button without a second variant, and it does not fight the
 * palette the way a green disc on an orange button did.
 *
 * The waves are genuinely knocked out of the disc rather than drawn over it,
 * which is what makes it the logo rather than a sketch of it — hence the mask.
 * The id comes from `useId`, because two of these on one page with a hardcoded
 * id would share a mask; `Flag.tsx` carries the longer version of that story.
 *
 * It travels **beside** an external-link arrow rather than replacing it. The
 * mark says where; the arrow says that you are leaving.
 */
export const IconSpotify = ({ size = 18 }: Props) => {
  const id = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <mask id={id}>
        <circle cx="12" cy="12" r="11" fill="#fff" />
        <g fill="none" stroke="#000" strokeWidth="1.9" strokeLinecap="round">
          <path d="M6.8 8.7c3.5-1 7.7-.7 10.7 1.2" />
          <path d="M7.7 12.2c2.9-.8 6.3-.5 8.8 1.1" />
          <path d="M8.5 15.5c2.3-.6 4.9-.4 6.9.8" />
        </g>
      </mask>
      <circle cx="12" cy="12" r="11" fill="currentColor" mask={`url(#${id})`} />
    </svg>
  );
};

export const IconMusic = (p: Props) => <S {...p}><path d="M9 18V5l10-2v13" /><circle cx="6.5" cy="18" r="2.5" /><circle cx="16.5" cy="16" r="2.5" /></S>;
export const IconExternal = (p: Props) => <S {...p}><path d="M7 17L17 7M9 7h8v8" /></S>;
export const IconShield = (p: Props) => <S {...p}><path d="M12 3l7 3v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6z" /><path d="M9.2 12.2l2 2 3.6-3.9" /></S>;
export const IconInfo = (p: Props) => <S {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v.01M12 11v5" /></S>;
export const IconCheck = (p: Props) => <S {...p}><path d="M5 13l4 4 10-10" /></S>;
export const IconClose = (p: Props) => <S {...p}><path d="M6 6l12 12M18 6L6 18" /></S>;
export const IconPlus = (p: Props) => <S {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></S>;
export const IconWifiOff = (p: Props) => (
  <S {...p}><path d="M3 3l18 18" /><path d="M5 12.5a10 10 0 0 1 4-2.4M12 17.5v.01M8.7 14.7a6 6 0 0 1 2.3-1.1" /></S>
);
export const IconLocate = (p: Props) => <S {...p}><circle cx="12" cy="12" r="7" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></S>;
export const IconTrash = (p: Props) => <S {...p}><path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" /></S>;
export const IconPencil = (p: Props) => <S {...p}><path d="M4 20h4L18.5 9.5l-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></S>;
export const IconHeart = (p: Props) => <S {...p}><path d="M12 20.4S4.5 15.6 4.5 10.6A3.9 3.9 0 0 1 12 8.4a3.9 3.9 0 0 1 7.5 2.2c0 5-7.5 9.8-7.5 9.8z" /></S>;
/** A broken chain — "unmatch", which detaches an account from a roster row.
 *  Deliberately not a ✕: on a card carrying somebody's name, a cross reads as
 *  "delete this guest" rather than "undo this match", and the action is one an
 *  organiser reaches for while resolving a dispute. */
/* A wrapped box, for the Gifts destination (D63). Stroked at the set's own
   weight rather than filled, because it sits in the header between IconImages
   and the avatar — a filled mark there would read as the selected tab. */
export const IconGift = (p: Props) => (
  <S {...p}>
    <rect x="3.5" y="7.5" width="17" height="4" rx="1.5" />
    <path d="M5.2 11.5v7.3a1.7 1.7 0 0 0 1.7 1.7h10.2a1.7 1.7 0 0 0 1.7-1.7v-7.3" />
    <path d="M12 7.5v13" />
    <path d="M12 7.5S11.3 4 9.2 4a1.9 1.9 0 0 0 0 3.5zM12 7.5S12.7 4 14.8 4a1.9 1.9 0 0 1 0 3.5z" />
  </S>
);
export const IconUnlink = (p: Props) => (
  <S {...p}>
    <path d="M10 13.8l-2 2a3.4 3.4 0 1 1-4.8-4.8l2-2" />
    <path d="M14 10.2l2-2a3.4 3.4 0 1 1 4.8 4.8l-2 2" />
    <path d="M9 4.6V2.4M4.6 9H2.4M15 19.4v2.2M19.4 15h2.2" />
  </S>
);
export const IconUser = (p: Props) => <S {...p}><circle cx="12" cy="8.5" r="3.6" /><path d="M5 20a7 7 0 0 1 14 0" /></S>;
/* A leaf, for the things the resort puts on beside the wedding. Not a pair of
   hands or a lotus: both read as a spa logo at 16px, and this has to sit on one
   quiet line without becoming the loudest thing in the rail. */
export const IconLeaf = (p: Props) => <S {...p}><path d="M5 19c0-8 5-13 14-14 1 9-4 15-14 14z" /><path d="M5 19c3-3 6-5 9-6" /></S>;
/* A bubble with three dots — something being said, and still being said. Two
   overlapping bubbles were the first draft and turned to noise at 19px, where
   the crossing outlines read as one broken shape. The dots are `h.01` segments
   rather than circles, which the set's round line caps render as dots at the
   stroke weight, so they thicken with everything else. */
export const IconChat = (p: Props) => <S {...p}><path d="M3.5 8a3.5 3.5 0 0 1 3.5-3.5h10A3.5 3.5 0 0 1 20.5 8v5a3.5 3.5 0 0 1-3.5 3.5h-6l-4.5 3.5v-3.5A3.5 3.5 0 0 1 3.5 13z" /><path d="M8 10.5h.01M12 10.5h.01M16 10.5h.01" /></S>;

export const IconPlay = ({ size = 16 }: Props) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
);
export const IconGithub = ({ size = 14 }: Props) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 0 0-3.2 19.5c.5.1.7-.2.7-.5v-1.8c-2.8.6-3.4-1.3-3.4-1.3-.4-1.1-1.1-1.4-1.1-1.4-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.7.4-1.1.6-1.4-2.2-.3-4.6-1.1-4.6-5 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.6 0 0 .8-.3 2.7 1a9.3 9.3 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.3.2 2.3.1 2.6.6.7 1 1.6 1 2.7 0 3.9-2.4 4.7-4.6 5 .4.3.7.9.7 1.9v2.8c0 .3.2.6.7.5A10 10 0 0 0 12 2z" />
  </svg>
);

/* ─── the decorative marks (D53) ───────────────────────────────────────────
   These replace the card suits — ♠ ♥ ♦ — that stood on the empty states and
   the 404 while teams were suits.

   **Drawn rather than set as emoji, and that is the whole point.** The suits
   were text: `.empty .mark` colours them with `var(--color-accent)` at 50%
   opacity, and `.reveal-mark` ghosts its glyph at 22%. An emoji ignores both —
   ⚽ renders as its own full-colour bitmap whatever CSS asks for, and renders
   differently on iOS, Android and Windows. These inherit `currentColor` and the
   opacity above them, so every empty state keeps the exact weight it has today.

   Filled, not stroked, unlike the rest of this file: at the 44px of an empty
   state and the 230px of the reveal, a 2.75 stroke reads as hairline wireframe
   next to the solid glyph it replaces. */

function F({ size = 19, className, children }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="currentColor"
      className={className} aria-hidden="true" focusable="false"
    >
      {children}
    </svg>
  );
}

/** The default mark. A ball, for a tournament named after one.
 *
 *  **Cut out of a solid disc, not built up from outlines.** Drawn as a rim plus
 *  spokes it came out a ship's wheel, and adding panel caps to fix that made it
 *  a cog. The pentagon has to be the hole for the eye to read "football" — and a
 *  solid silhouette also matches the weight of the ♠ ♥ ♦ it replaces, which were
 *  solid glyphs at the same 50% opacity.
 *
 *  `fillRule="evenodd"` is what turns the inner subpaths into holes, and it is
 *  why the whole thing is one `d` rather than five rotated children: a
 *  `transform` cannot apply to a subpath. The seam coordinates are therefore
 *  pre-rotated. They stop at r≈9.6, short of the 10.2 rim, so each one cuts the
 *  disc instead of crossing its edge — a subpath that crossed the rim would
 *  toggle the region *outside* the circle and fill it. */
export const MarkBall = (p: Props) => (
  <F {...p}>
    <path fillRule="evenodd" d="M12 1.8a10.2 10.2 0 1 0 0 20.4 10.2 10.2 0 0 0 0-20.4zM12.00 7.80 15.99 10.70 14.47 15.40 9.53 15.40 8.01 10.70zM11.20 7.60 12.80 7.60 12.80 2.40 11.20 2.40zM15.94 9.88 16.43 11.40 21.38 9.79 20.88 8.27zM15.23 15.09 13.94 16.03 17.00 20.24 18.29 19.30zM10.06 16.03 8.77 15.09 5.71 19.30 7.00 20.24zM7.57 11.40 8.06 9.88 3.12 8.27 2.62 9.79z" />
  </F>
);

/** For the wall and anywhere a result is being celebrated. */
export const MarkTrophy = (p: Props) => (
  <F {...p}>
    <path d="M6.5 2h11v1.5H21a1 1 0 0 1 1 1V7a4 4 0 0 1-3.6 3.98A6.51 6.51 0 0 1 13 15.4V19h3.2a1 1 0 0 1 1 1v1.5H6.8V20a1 1 0 0 1 1-1H11v-3.6a6.51 6.51 0 0 1-5.4-4.42A4 4 0 0 1 2 7V4.5a1 1 0 0 1 1-1h3.5zm0 3H4v2a2 2 0 0 0 1.6 1.96A24 24 0 0 1 6.5 5zm11 0a24 24 0 0 1 .9 3.96A2 2 0 0 0 20 7V5z" />
  </F>
);

/** For the upload screen — what you did to get the photograph. */
export const MarkBoot = (p: Props) => (
  <F {...p}>
    <path d="M3 5.5A1.5 1.5 0 0 1 4.5 4h3.2a1.5 1.5 0 0 1 1.46 1.15l.83 3.44 4.2 1.26a6.5 6.5 0 0 1 4.6 5.36l.2 1.6a1.5 1.5 0 0 1-1.49 1.69H4.5A1.5 1.5 0 0 1 3 17zm2 13.1h14.4a.6.6 0 0 1 .6.6v.9H4.4v-.9a.6.6 0 0 1 .6-.6z" />
  </F>
);

/** For the schedule — the whistle that starts things.
 *  No lanyard: at 44px and half opacity the cord separated from the body and
 *  read as a stray dash rather than as string. */
export const MarkWhistle = (p: Props) => (
  <F {...p}>
    <path d="M9.4 5.9h11.3a1.3 1.3 0 0 1 1.3 1.3v2.4a1.3 1.3 0 0 1-1.3 1.3h-1.5a7.1 7.1 0 1 1-9.8-5zm-.5 4.3a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8z" />
  </F>
);
